import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MDBox from "components/MDBox";

export default function VideoPanel({
  remoteStreams,
  callStreamId,
  darkMode,
  videoPanelHeight,
  onResizeStart,
  showMaximizeControl = false,
  isMaximized = false,
  onToggleMaximize,
}) {
  return (
    <>
      {remoteStreams
        .filter(
          (r) => r.stream && !r.hasVideo && r.id !== callStreamId && !r.isLocalEcho,
        )
        .map((r) => (
          <audio
            key={r.id}
            autoPlay
            playsInline
            ref={(el) => {
              if (el && r.stream && el.srcObject !== r.stream) {
                el.srcObject = r.stream;
                try {
                  el.play?.().catch(() => {});
                } catch {}
              }
            }}
            style={{ display: "none" }}
          />
        ))}

      <MDBox
        id="video-block"
        sx={{
          width: "100%",
          height: `${videoPanelHeight}px`,
          display: "flex",
          gap: 1,
          borderRadius: 3,
          overflow: "hidden",
          alignItems: "stretch",
          background: darkMode
            ? "linear-gradient(135deg, rgba(20, 40, 70, 0.8) 0%, rgba(15, 52, 96, 0.8) 100%)"
            : "linear-gradient(135deg, rgba(200, 220, 240, 0.5) 0%, rgba(180, 210, 240, 0.5) 100%)",
          border: `2.5px solid ${
            darkMode
              ? "rgba(66, 165, 245, 0.4)"
              : "rgba(33, 150, 243, 0.3)"
          }`,
          boxShadow: darkMode
            ? "inset 0 2px 12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(66, 165, 245, 0.1), 0 8px 24px rgba(0, 0, 0, 0.3)"
            : "inset 0 2px 12px rgba(33, 150, 243, 0.1), inset 0 1px 0 rgba(66, 165, 245, 0.15), 0 4px 16px rgba(33, 150, 243, 0.1)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background: darkMode
              ? "radial-gradient(circle at 20% 50%, rgba(66, 165, 245, 0.05) 0%, transparent 50%)"
              : "radial-gradient(circle at 20% 50%, rgba(33, 150, 243, 0.03) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        {showMaximizeControl && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 3,
            }}
          >
            <Tooltip
              title={isMaximized ? "Minimize video panel" : "Maximize video panel"}
            >
              <IconButton
                size="small"
                onClick={onToggleMaximize}
                sx={{
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                {isMaximized ? (
                  <FullscreenExitIcon fontSize="small" />
                ) : (
                  <FullscreenIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {remoteStreams
          .filter(
            (r) => r.stream && r.hasVideo && r.id !== callStreamId && !r.isLocalEcho,
          )
          .map((r) => (
            <video
              key={r.id}
              autoPlay
              playsInline
              ref={(el) => {
                if (el && r.stream && el.srcObject !== r.stream) {
                  el.srcObject = r.stream;
                  el.muted = false;
                  el.play?.().catch(() => {});
                }
              }}
              style={{
                flex: 1,
                minWidth: 0,
                height: "100%",
                objectFit: "contain",
                borderRadius: "5px",
              }}
            />
          ))}
      </MDBox>

      <Box
        onPointerDown={onResizeStart}
        onTouchStart={onResizeStart}
        onMouseDown={onResizeStart}
        sx={{
          height: 12,
          width: "100%",
          cursor: "row-resize",
          touchAction: "none",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
          mt: 1,
            borderRadius: 2,
            background: darkMode
              ? "linear-gradient(90deg, rgba(66, 165, 245, 0.2), rgba(33, 150, 243, 0.35), rgba(66, 165, 245, 0.2))"
              : "linear-gradient(90deg, rgba(33, 150, 243, 0.2), rgba(25, 118, 210, 0.35), rgba(33, 150, 243, 0.2))",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              width: "40px",
              height: "4px",
              borderRadius: "2px",
              background: darkMode
                ? "rgba(66, 165, 245, 0.6)"
                : "rgba(33, 150, 243, 0.6)",
            },
            "&:hover": {
              background: darkMode
                ? "linear-gradient(90deg, rgba(66, 165, 245, 0.35), rgba(33, 150, 243, 0.5), rgba(66, 165, 245, 0.35))"
                : "linear-gradient(90deg, rgba(33, 150, 243, 0.3), rgba(25, 118, 210, 0.5), rgba(33, 150, 243, 0.3))",
              boxShadow: "0 2px 8px rgba(33, 150, 243, 0.25)",
              "&::before": {
                background: darkMode
                  ? "rgba(66, 165, 245, 0.8)"
                  : "rgba(33, 150, 243, 0.8)",
              },
            },
            "&:active": {
              transform: "scaleY(1.3)",
              background: darkMode
                ? "linear-gradient(90deg, rgba(66, 165, 245, 0.45), rgba(33, 150, 243, 0.6), rgba(66, 165, 245, 0.45))"
                : "linear-gradient(90deg, rgba(33, 150, 243, 0.4), rgba(25, 118, 210, 0.6), rgba(33, 150, 243, 0.4))",
              boxShadow: "0 3px 12px rgba(33, 150, 243, 0.4)",
              "&::before": {
                background: darkMode
                  ? "rgba(66, 165, 245, 1)"
                  : "rgba(33, 150, 243, 1)",
                height: "5px",
              },
            },
          }}
        />
    </>
  );
}
